import { useEffect } from 'react';

import type { Language } from '../i18n/types';

export const SITE_ORIGIN = 'https://medhub.tgl.com.sa';
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/images/TabIcon.jpg`;

interface LayoutSeoOptions {
  language: Language;
  pathname: string;
  ogImage?: string;
}

interface PageMetaOptions {
  title: string;
  description: string;
}

function upsertMeta(attr: 'name' | 'property', key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function upsertLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function stripLanguagePrefix(pathname: string, language: Language): string {
  const prefix = `/${language}`;
  if (pathname === prefix) return '';
  if (pathname.startsWith(`${prefix}/`)) return pathname.slice(prefix.length);
  return '';
}

export function useLayoutSeo({
  language,
  pathname,
  ogImage,
}: LayoutSeoOptions) {
  useEffect(() => {
    const subPath = stripLanguagePrefix(pathname, language);
    const canonical = `${SITE_ORIGIN}/${language}${subPath}`;
    const arHref = `${SITE_ORIGIN}/ar${subPath}`;
    const enHref = `${SITE_ORIGIN}/en${subPath}`;
    const ogImg = ogImage ?? DEFAULT_OG_IMAGE;
    const locale = language === 'ar' ? 'ar_SA' : 'en_US';
    const altLocale = language === 'ar' ? 'en_US' : 'ar_SA';

    document.documentElement.setAttribute('lang', language);
    document.documentElement.setAttribute(
      'dir',
      language === 'ar' ? 'rtl' : 'ltr',
    );

    upsertLink('canonical', canonical);
    upsertLink('alternate', arHref, 'ar');
    upsertLink('alternate', enHref, 'en');
    upsertLink('alternate', arHref, 'x-default');

    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', ogImg);
    upsertMeta('property', 'og:locale', locale);
    upsertMeta('property', 'og:locale:alternate', altLocale);
    upsertMeta('name', 'twitter:image', ogImg);
  }, [language, pathname, ogImage]);
}

export function usePageMeta({ title, description }: PageMetaOptions) {
  useEffect(() => {
    document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
  }, [title, description]);
}

function escapeJsonLd(json: string): string {
  return json
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');
}

export function JsonLd({
  data,
}: {
  data: Record<string, unknown> | Record<string, unknown>[];
}) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: escapeJsonLd(JSON.stringify(data)) }}
    />
  );
}
